// Note: renderMonthElement prop type definition is not correct in the DayPickerRangeController component. Therefore @ts-nocheck has been added.
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck
import 'react-dates/initialize';
import 'react-dates/lib/css/_datepicker.css';
import {
  FC,
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  DayPickerRangeController,
  DayPickerRangeControllerShape,
} from 'react-dates';
import moment from 'moment';

import { CalendarHolidaysViewModel } from '@api/models/CalendarHolidaysViewModel';
import { CalendarHolidayItemViewModel } from '@api/models/CalendarHolidayItemViewModel';
import { useBreakpoint } from '@contexts/breakpoints';
import {
  DateType,
  DEFAULT_DATE_FORMAT,
  URL_DATE_FORMAT,
  formatLocalDate,
  formatMomentDate,
  getCurrentDate,
  isInclusivelyAfterDay,
} from '@utils/dateUtil';
import { ChevronLeft, ChevronRight, Compare } from '@assets/icons';
import CustomIcon from '@components/utils/CustomIcon';
import { FlexibleDiv, Row } from '@components/formStyles';

import {
  CalendarHolidayFooterDot,
  CalendarHolidayFooterItem,
  CalendarHolidayFooterSummary,
  DatePickerDayOfWeekLabel,
  DatePickerMonthLabel,
  DateRangePickerFooter,
  DateRangePickerWrapper,
  Holiday,
  HolidayCard,
  HolidayCardFooter,
  HolidayCardMeta,
  HolidayChevron,
  HolidayContainer,
  HolidayDateRange,
  HolidayDescriptionBadge,
  HolidayDescriptionBadges,
  HolidayDaysBadge,
  HolidayDayContent,
  HolidayName,
  HolidayPanelScroll,
  HolidayTab,
  HolidayTabBar,
  HolidayTabLabel,
  HolidayTabRadio,
  WeekHolidayChipBold,
  WeekHolidayChipRegular,
  NavNextButton,
  NavPrevButton,
  StyledButton,
  WeekHoliday,
  WeekHolidayCard,
  WeekHolidayChipRow,
  WeekHolidayMonthGroup,
  WeekHolidayMonthHeader,
} from '../DatePickerStyle';
import { renderCalendarDay } from '../CalendarDayText';
import {
  buildHolidayDateSet,
  getHolidayForDate,
  holidayCellMarkers,
  parseHolidayPayloadDate,
  renderCalendarEventFast,
} from '../helpers';
import { renderNavButton } from '../renderNavButton';
import { useCalendarReadiness } from '../useCalendarReadiness';
import 'moment/locale/tr';
type RequiredProps = 'focusedInput' | 'onFocusChange' | 'initialVisibleMonth';

export type DateRangeApplyResolver = (context: {
  startDate: DateType | null;
  endDate: DateType | null;
  minimumNights: number;
  isOutsideRange: (day: DateType) => boolean;
  isDayBlocked?: (day: DateType) => boolean;
}) => {
  startDate: DateType;
  endDate: DateType | null;
} | null;

export type DayPickerRangeControllerProps = Omit<
  DayPickerRangeControllerShape,
  RequiredProps
> &
  Partial<Pick<DayPickerRangeControllerShape, RequiredProps>> & {
    initialFocusedInput?: 'startDate' | 'endDate' | null;
    isDynamicFocus?: boolean;
    initialData?: CalendarHolidaysViewModel;
    isHoliday?: boolean;
    showHolidayTabs?: boolean;
    showHolidayModeShadow?: boolean;
    showFooter?: boolean;
    useDesktopSearchbarFooter?: boolean;
    holidayPanelFillsAvailableSpace?: boolean;
    onVisibleHolidayNamesChange?: (holidayNames: string[]) => void;
    resolveRangeOnApply?: DateRangeApplyResolver;
    onReady?: () => void;
  };

// CORR-1: Holiday tab toggle. Labels/order are locked by SDLC IK-5 and must not
// be rebranded. "Takvim" is the default active tab and shows the calendar; the
// other two are placeholders until CORR-2 fills their card/chip grids.
type HolidayTabKey = 'calendar' | 'official' | 'weekend';

type WeekendChipSegment = {
  text: string;
  weight: 'bold' | 'regular';
};

const HOLIDAY_TABS: { key: HolidayTabKey; label: string }[] = [
  { key: 'calendar', label: 'Takvim' },
  { key: 'official', label: 'Resmi Tatiller' },
  { key: 'weekend', label: 'Hafta Sonu Tatilleri' },
];

const DateRangePicker: FC<DayPickerRangeControllerProps> = ({
  startDate = null,
  endDate = null,
  daySize = 33,
  onDatesChange = () => false,
  minimumNights = 1,
  isOutsideRange = (day: DateType) =>
    !isInclusivelyAfterDay(day, getCurrentDate()),
  enableOutsideDays = false,
  daysViolatingMinNightsCanBeClicked = false,
  orientation = 'horizontal',
  withPortal = false,
  numberOfMonths = 2,
  keepOpenOnDateSelect = false,
  isRTL = false,
  renderNavPrevButton = renderNavButton(NavPrevButton, ChevronLeft),
  renderNavNextButton = renderNavButton(NavNextButton, ChevronRight),
  monthFormat = 'MMMM YYYY',
  hideKeyboardShortcutsPanel = true,
  initialVisibleMonth = null,
  initialFocusedInput,
  onFocusChange,
  onClose,
  onReady,
  initialData,
  isHoliday = false,
  showHolidayTabs,
  showHolidayModeShadow = true,
  showFooter = true,
  useDesktopSearchbarFooter = false,
  holidayPanelFillsAvailableSpace = false,
  onVisibleHolidayNamesChange,
  resolveRangeOnApply,
  // CORR-8: `renderDayContents` must be captured here (not left to fall into
  // `...props`) — otherwise a caller-provided renderer (e.g. AvailableRoomDates'
  // availability-status colors) lands in the rest spread on
  // `DayPickerRangeController` AFTER the holiday-aware `renderDayContents` below
  // and silently overrides it, so the holiday dot never renders for that caller.
  renderDayContents: externalRenderDayContents,
  ...props
}) => {
  const { xl } = useBreakpoint();
  const readinessRef = useCalendarReadiness(onReady);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const [visibleMonthKeys, setVisibleMonthKeys] = useState<string[]>([]);
  const [desktopVisibleStartMonthKey, setDesktopVisibleStartMonthKey] =
    useState<string | null>(null);

  // GSF-WEB-003: Pre-compute the CMS holiday payload into a date-keyed Map once
  // (GSF-WEB-005) so each calendar cell does an O(1) lookup during render.
  // Takvim sekmesinde yalnız resmi tatil marker'ları görünür olmalı. Hafta sonu
  // tatilleri, yalnız "Hafta Sonu Tatilleri" sekmesindeki chip akışında görünür.
  const calendarTabHolidayMap = useMemo(
    () =>
      buildHolidayDateSet({
        calendarHoliday: initialData?.calendarHoliday ?? [],
        weekendHoliday: [],
      }),
    [initialData]
  );

  // GSF-WEB-007 / CORR-2: Clicking a holiday card or chip jumps the search form
  // to the CMS-provided redirected travel window
  // (`redirectedStartDate`/`redirectedEndDate`). The holiday's own
  // `holidayStartDate`/`holidayEndDate` are used ONLY as a fallback when the
  // redirected values are missing.
  const dateSelected = (item: CalendarHolidayItemViewModel) => {
    const start = item.redirectedStartDate ?? item.holidayStartDate;
    const end = item.redirectedEndDate ?? item.holidayEndDate;

    const selectedStart = start
      ? parseHolidayPayloadDate(start, URL_DATE_FORMAT)
      : null;
    const selectedEnd = end
      ? parseHolidayPayloadDate(end, URL_DATE_FORMAT)
      : null;

    onDatesChange({
      startDate: selectedStart,
      endDate: selectedEnd,
    });

    onClose?.({ startDate: selectedStart, endDate: selectedEnd });
  };

  const [focusedInput, setFocusedInput] = useState<
    'startDate' | 'endDate' | null
  >(initialFocusedInput || 'startDate');

  // CORR-1: Locally managed active holiday tab (defaults to "Takvim").
  const [activeHolidayTab, setActiveHolidayTab] =
    useState<HolidayTabKey>('calendar');

  // Holiday capability and tab visibility are separate: a caller may keep CMS
  // day markers and holiday-mode calendar styling while omitting the three-tab
  // navigation. Existing callers retain the previous behavior by default.
  const shouldShowHolidayTabs = showHolidayTabs ?? isHoliday;

  // CORR-2: "Resmi Tatiller" card grid source (official holidays).
  const officialHolidays = initialData?.calendarHoliday ?? [];

  // CORR-2: "Hafta Sonu Tatilleri" grouped by month (BFF already applies the
  // 6-month window). Grouping key keeps year separation, the header shows the
  // Turkish month name only (e.g. "Aralık"). Insertion order is preserved.
  const groupedWeekendHolidays = useMemo(() => {
    const groups = new Map<
      string,
      { label: string; items: CalendarHolidayItemViewModel[] }
    >();

    (initialData?.weekendHoliday ?? []).forEach((item) => {
      const start = parseHolidayPayloadDate(
        item.holidayStartDate,
        URL_DATE_FORMAT
      ).locale('tr');
      const key = start.isValid() ? start.format('YYYY-MM') : 'unknown';
      const label = start.isValid() ? start.format('MMMM') : '';

      if (!groups.has(key)) groups.set(key, { label, items: [] });
      groups.get(key)?.items.push(item);
    });

    return Array.from(groups.values());
  }, [initialData]);

  const visibleOfficialHolidayNames = useMemo(() => {
    if (!shouldShowHolidayTabs || activeHolidayTab !== 'calendar') return [];
    const firstVisibleMonthKey =
      orientation === 'verticalScrollable'
        ? visibleMonthKeys[0]
        : desktopVisibleStartMonthKey ?? visibleMonthKeys[0];

    if (!firstVisibleMonthKey) return [];

    const monthStart = moment(firstVisibleMonthKey, 'YYYY-MM').startOf('month');
    const monthEnd = monthStart.clone().endOf('month');

    return officialHolidays
      .filter((item) => {
        const start = parseHolidayPayloadDate(
          item.holidayStartDate,
          URL_DATE_FORMAT
        );
        const end = parseHolidayPayloadDate(
          item.holidayEndDate ?? item.holidayStartDate,
          URL_DATE_FORMAT
        );

        return (
          start.isValid() &&
          end.isValid() &&
          start.isSameOrBefore(monthEnd, 'day') &&
          end.isSameOrAfter(monthStart, 'day')
        );
      })
      .map((item) => item.holidayName?.trim())
      .filter((name): name is string => Boolean(name))
      .filter((name, index, array) => array.indexOf(name) === index);
  }, [
    activeHolidayTab,
    desktopVisibleStartMonthKey,
    officialHolidays,
    orientation,
    shouldShowHolidayTabs,
    visibleMonthKeys,
  ]);

  const shiftDesktopVisibleStartMonth = useCallback(
    (offset: number) => {
      if (orientation === 'verticalScrollable') return;

      setDesktopVisibleStartMonthKey((current) => {
        const baseMonthKey = current ?? visibleMonthKeys[0];
        if (!baseMonthKey) return current;

        return moment(baseMonthKey, 'YYYY-MM')
          .add(offset, 'month')
          .format('YYYY-MM');
      });
    },
    [orientation, visibleMonthKeys]
  );

  const measureVisibleMonths = useCallback(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;

    const viewport =
      wrapper.querySelector(
        '.DayPicker_transitionContainer__verticalScrollable'
      ) ?? wrapper;
    const viewportRect = viewport.getBoundingClientRect();
    const monthNodes = Array.from(
      wrapper.querySelectorAll<HTMLElement>('[data-calendar-month-key]')
    );

    // JSDOM does not calculate layout metrics for these nodes, so tests would
    // otherwise see no visible months. Fall back to the rendered month order.
    if (viewportRect.width === 0 && viewportRect.height === 0) {
      const fallbackKeys = monthNodes
        .map((node) => node.dataset.calendarMonthKey)
        .filter((key): key is string => Boolean(key))
        .slice(0, orientation === 'verticalScrollable' ? 1 : numberOfMonths);

      setVisibleMonthKeys((current) =>
        current.join('|') === fallbackKeys.join('|') ? current : fallbackKeys
      );
      return;
    }

    const visibleMonthEntries = monthNodes
      .map((node) => ({
        key: node.dataset.calendarMonthKey,
        rect: node.getBoundingClientRect(),
      }))
      .filter(
        ({ key, rect }) =>
          !!key &&
          rect.right > viewportRect.left &&
          rect.left < viewportRect.right &&
          rect.bottom > viewportRect.top &&
          rect.top < viewportRect.bottom
      )
      .sort((left, right) => {
        if (Math.abs(left.rect.top - right.rect.top) < 1) {
          return left.rect.left - right.rect.left;
        }

        return left.rect.top - right.rect.top;
      });

    const nextKeys = visibleMonthEntries
      .reduce<Array<string>>((keys, entry) => {
        const previousEntry = visibleMonthEntries
          .slice(0, visibleMonthEntries.indexOf(entry))
          .reverse()
          .find(
            (candidate) =>
              Math.abs(candidate.rect.top - entry.rect.top) < 1 &&
              Math.abs(candidate.rect.left - entry.rect.left) < 1
          );

        if (!entry.key) return keys;

        if (!previousEntry) {
          keys.push(entry.key);
          return keys;
        }

        keys[keys.length - 1] = entry.key;
        return keys;
      }, [])
      .filter((key, index, array) => array.indexOf(key) === index);

    setVisibleMonthKeys((current) =>
      current.join('|') === nextKeys.join('|') ? current : nextKeys
    );
  }, []);

  useEffect(() => {
    if (orientation === 'verticalScrollable') {
      setDesktopVisibleStartMonthKey(null);
      return;
    }

    if (!shouldShowHolidayTabs || activeHolidayTab !== 'calendar') {
      setDesktopVisibleStartMonthKey(null);
      return;
    }

    if (visibleMonthKeys.length === 0) return;

    setDesktopVisibleStartMonthKey((current) => current ?? visibleMonthKeys[0]);
  }, [activeHolidayTab, orientation, shouldShowHolidayTabs, visibleMonthKeys]);

  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper || !shouldShowHolidayTabs || activeHolidayTab !== 'calendar') {
      setVisibleMonthKeys([]);
      return;
    }

    const scheduleMeasure = () => {
      window.requestAnimationFrame(measureVisibleMonths);
      window.setTimeout(measureVisibleMonths, 0);
      window.setTimeout(measureVisibleMonths, 200);
    };
    const scrollTarget = wrapper.querySelector(
      '.DayPicker_transitionContainer__verticalScrollable'
    );

    scheduleMeasure();
    wrapper.addEventListener('click', scheduleMeasure, true);
    window.addEventListener('resize', scheduleMeasure);
    scrollTarget?.addEventListener('scroll', scheduleMeasure, {
      passive: true,
    });

    return () => {
      wrapper.removeEventListener('click', scheduleMeasure, true);
      window.removeEventListener('resize', scheduleMeasure);
      scrollTarget?.removeEventListener('scroll', scheduleMeasure);
    };
  }, [activeHolidayTab, measureVisibleMonths, shouldShowHolidayTabs]);

  useEffect(() => {
    onVisibleHolidayNamesChange?.(visibleOfficialHolidayNames);
  }, [onVisibleHolidayNamesChange, visibleOfficialHolidayNames]);

  const handlePrevMonthClick = useCallback(
    (event, onClick) => {
      shiftDesktopVisibleStartMonth(-1);
      onClick?.(event);
    },
    [shiftDesktopVisibleStartMonth]
  );

  const handleNextMonthClick = useCallback(
    (event, onClick) => {
      shiftDesktopVisibleStartMonth(1);
      onClick?.(event);
    },
    [shiftDesktopVisibleStartMonth]
  );

  // Official-holiday card date range → "DD.MM.YYYY - DD.MM.YYYY" (single day
  // collapses to one date).
  const formatHolidayDateRange = (item: CalendarHolidayItemViewModel) => {
    const start = item.holidayStartDate
      ? parseHolidayPayloadDate(item.holidayStartDate, URL_DATE_FORMAT).format(
          DEFAULT_DATE_FORMAT
        )
      : '';
    const end = item.holidayEndDate
      ? parseHolidayPayloadDate(item.holidayEndDate, URL_DATE_FORMAT).format(
          DEFAULT_DATE_FORMAT
        )
      : '';

    if (!end || end === start) return start;
    return `${start} - ${end}`;
  };

  // Weekend chip label → "14 - 15 Aralık" (same month) or "30 Kasım - 1 Aralık"
  // (spanning two months); single day collapses to "14 Aralık". Split into a
  // bold (day-range digits) and regular (month name) part so the chip can
  // render the two font weights Figma (97:1777) specifies.
  const formatWeekendChipLabel = (
    item: CalendarHolidayItemViewModel
  ): WeekendChipSegment[] => {
    const start = parseHolidayPayloadDate(
      item.holidayStartDate,
      URL_DATE_FORMAT
    ).locale('tr');
    const end = parseHolidayPayloadDate(
      item.holidayEndDate ?? item.holidayStartDate,
      URL_DATE_FORMAT
    ).locale('tr');

    if (!start.isValid())
      return [{ text: item.holidayName ?? '', weight: 'regular' }];
    if (!end.isValid() || start.isSame(end, 'day'))
      return [
        { text: start.format('DD'), weight: 'bold' },
        { text: start.format('MMMM'), weight: 'regular' },
      ];
    if (start.month() === end.month())
      return [
        {
          text: `${start.format('DD')} - ${end.format('DD')}`,
          weight: 'bold',
        },
        { text: end.format('MMMM'), weight: 'regular' },
      ];
    return [
      { text: start.format('DD'), weight: 'bold' },
      { text: start.format('MMMM'), weight: 'regular' },
      { text: `- ${end.format('DD')}`, weight: 'bold' },
      { text: end.format('MMMM'), weight: 'regular' },
    ];
  };

  // CORR-8: Compose the "Takvim" tab's per-day content. When a caller supplies
  // its own `renderDayContents` (e.g. AvailableRoomDates' availability-status
  // colors) AND holiday mode is on, render BOTH the caller's content and the
  // holiday dot together instead of one silently overriding the other.
  // Callers without their own renderer (HotelBookerDatePicker,
  // ResponsiveDateRangeSelect, ResponsiveListingSearchDate) keep the existing
  // holiday-only / plain-day behavior unchanged.
  const renderDay = (day: DateType) => {
    if (!isHoliday) {
      return externalRenderDayContents
        ? externalRenderDayContents(day)
        : renderCalendarDay(day);
    }

    if (!externalRenderDayContents) {
      return renderCalendarEventFast(
        day,
        calendarTabHolidayMap,
        holidayCellMarkers
      );
    }

    const entry = getHolidayForDate(day, calendarTabHolidayMap);
    const Marker =
      entry?.type === 'weekend'
        ? holidayCellMarkers.WeekHoliday
        : entry?.type === 'holiday'
        ? holidayCellMarkers.Holiday
        : null;

    return (
      <HolidayDayContent>
        {externalRenderDayContents(day)}
        {Marker && entry ? <Marker item={entry.item} /> : null}
      </HolidayDayContent>
    );
  };

  const [drawerContentHeight, setDrawerContentHeight] = useState<number | null>(
    null
  );

  const findDrawerContentHeight = () => {
    if (typeof window !== 'undefined') {
      const el = document.getElementsByClassName('setur-drawer-body')[0];
      return el?.clientHeight;
    }
  };

  const handleApply = () => {
    if (!startDate) return;

    const resolvedRange = resolveRangeOnApply
      ? resolveRangeOnApply({
          startDate,
          endDate,
          minimumNights,
          isOutsideRange,
          isDayBlocked: props.isDayBlocked,
        })
      : { startDate, endDate };

    if (!resolvedRange) return;

    const isSameStartDate = resolvedRange.startDate.isSame(startDate, 'day');
    const isSameEndDate =
      resolvedRange.endDate === endDate ||
      (!!resolvedRange.endDate &&
        !!endDate &&
        resolvedRange.endDate.isSame(endDate, 'day'));

    if (!isSameStartDate || !isSameEndDate) {
      onDatesChange(resolvedRange);
    }

    onClose?.(resolvedRange);
  };

  useEffect(() => {
    const updateHeight = () => {
      setDrawerContentHeight(findDrawerContentHeight() as number);
    };

    updateHeight();

    window.addEventListener('resize', updateHeight);
    return () => window.removeEventListener('resize', updateHeight);
  }, []);

  return (
    <DateRangePickerWrapper
      ref={(node) => {
        wrapperRef.current = node;

        if (typeof readinessRef === 'function') {
          readinessRef(node);
        } else if (readinessRef) {
          readinessRef.current = node;
        }
      }}
      contentHeight={drawerContentHeight as number}
      $holidayMode={isHoliday}
      $showHolidayModeShadow={showHolidayModeShadow}
      $desktopSearchbarFooter={useDesktopSearchbarFooter}
      data-holiday-panel-fills-available-space={
        holidayPanelFillsAvailableSpace && activeHolidayTab !== 'calendar'
          ? 'true'
          : undefined
      }
    >
      {shouldShowHolidayTabs ? (
        <HolidayTabBar>
          {HOLIDAY_TABS.map((tab) => {
            const active = activeHolidayTab === tab.key;
            return (
              <HolidayTab
                key={tab.key}
                $active={active}
                onClick={() => setActiveHolidayTab(tab.key)}
              >
                <HolidayTabRadio
                  type='radio'
                  name='holiday-tab'
                  readOnly
                  checked={active}
                />
                <HolidayTabLabel $active={active}>{tab.label}</HolidayTabLabel>
              </HolidayTab>
            );
          })}
        </HolidayTabBar>
      ) : null}

      {shouldShowHolidayTabs && activeHolidayTab === 'official' ? (
        // "Resmi Tatiller": card grid of official holidays. Whole card clickable
        // → jumps the search form to the CMS redirected window (TASK-016).
        <HolidayPanelScroll
          data-fills-available-space={
            holidayPanelFillsAvailableSpace ? 'true' : undefined
          }
        >
          <Holiday>
            {officialHolidays.map((item, index) =>
              (() => {
                const holidayDescriptions =
                  item.selectHolidayDescription?.filter((description) =>
                    description?.name?.trim()
                  ) ?? [];

                return (
                  <HolidayCard
                    key={item.holidayStartDate ?? index}
                    onClick={() => dateSelected(item)}
                  >
                    <HolidayContainer>
                      <HolidayName>{item.holidayName}</HolidayName>
                      {item.numberOfDays ? (
                        <HolidayDaysBadge>
                          {item.numberOfDays} Gün
                        </HolidayDaysBadge>
                      ) : null}
                    </HolidayContainer>
                    {holidayDescriptions.length > 0 ? (
                      <HolidayCardMeta>
                        <HolidayDescriptionBadges>
                          {holidayDescriptions.map(
                            (description, descriptionIndex) => (
                              <HolidayDescriptionBadge
                                key={
                                  description.codename ??
                                  description.name ??
                                  descriptionIndex
                                }
                              >
                                {description.name}
                              </HolidayDescriptionBadge>
                            )
                          )}
                        </HolidayDescriptionBadges>
                        <HolidayCardFooter>
                          <HolidayDateRange>
                            {formatHolidayDateRange(item)}
                          </HolidayDateRange>
                          <HolidayChevron>
                            <CustomIcon
                              icon={ChevronRight}
                              fontSize={14}
                              paletteColor='grey1100'
                            />
                          </HolidayChevron>
                        </HolidayCardFooter>
                      </HolidayCardMeta>
                    ) : (
                      <HolidayCardFooter>
                        <HolidayDateRange>
                          {formatHolidayDateRange(item)}
                        </HolidayDateRange>
                        <HolidayChevron>
                          <CustomIcon
                            icon={ChevronRight}
                            fontSize={14}
                            paletteColor='grey1100'
                          />
                        </HolidayChevron>
                      </HolidayCardFooter>
                    )}
                  </HolidayCard>
                );
              })()
            )}
          </Holiday>
        </HolidayPanelScroll>
      ) : shouldShowHolidayTabs && activeHolidayTab === 'weekend' ? (
        // "Hafta Sonu Tatilleri": month-grouped chips. Whole chip clickable →
        // same redirect behavior as the official cards (TASK-016).
        <HolidayPanelScroll
          data-fills-available-space={
            holidayPanelFillsAvailableSpace ? 'true' : undefined
          }
        >
          <WeekHoliday>
            {groupedWeekendHolidays.map((group, groupIndex) => (
              <WeekHolidayMonthGroup key={group.label || groupIndex}>
                <WeekHolidayMonthHeader>{group.label}</WeekHolidayMonthHeader>
                <WeekHolidayChipRow>
                  {group.items.map((item, index) => {
                    const segments = formatWeekendChipLabel(item);
                    return (
                      <WeekHolidayCard
                        key={item.holidayStartDate ?? index}
                        onClick={() => dateSelected(item)}
                        aria-label={segments
                          .map((segment) => segment.text)
                          .join(' ')}
                      >
                        {segments.map((segment, segmentIndex) => (
                          <Fragment key={segmentIndex}>
                            {segment.weight === 'bold' ? (
                              <WeekHolidayChipBold>
                                {segment.text}
                              </WeekHolidayChipBold>
                            ) : (
                              <WeekHolidayChipRegular>
                                {segment.text}
                              </WeekHolidayChipRegular>
                            )}
                          </Fragment>
                        ))}
                      </WeekHolidayCard>
                    );
                  })}
                </WeekHolidayChipRow>
              </WeekHolidayMonthGroup>
            ))}
          </WeekHoliday>
        </HolidayPanelScroll>
      ) : (
        <DayPickerRangeController
          verticalBorderSpacing={3}
          initialVisibleMonth={initialVisibleMonth}
          focusedInput={props.focusedInput || focusedInput}
          onFocusChange={
            onFocusChange
              ? onFocusChange
              : (nextFocusedInput) => {
                  setFocusedInput(nextFocusedInput || 'startDate');
                }
          }
          startDate={startDate}
          endDate={endDate}
          renderDayContents={renderDay}
          onDatesChange={onDatesChange}
          renderMonthElement={({ month }) => (
            <DatePickerMonthLabel
              $holidayMode={isHoliday}
              data-calendar-month-key={moment(month).format('YYYY-MM')}
            >
              {moment(month).locale('tr').format('MMMM YYYY')}
            </DatePickerMonthLabel>
          )}
          weekDayFormat='d'
          renderWeekHeaderElement={(dayOfWeek) => (
            <DatePickerDayOfWeekLabel $holidayMode={isHoliday}>
              {moment().day(parseInt(dayOfWeek, 10)).locale('tr').format('dd')}
            </DatePickerDayOfWeekLabel>
          )}
          daySize={daySize}
          minimumNights={minimumNights}
          isOutsideRange={isOutsideRange}
          enableOutsideDays={enableOutsideDays}
          daysViolatingMinNightsCanBeClicked={
            daysViolatingMinNightsCanBeClicked
          }
          orientation={orientation}
          withPortal={withPortal}
          numberOfMonths={numberOfMonths}
          keepOpenOnDateSelect={keepOpenOnDateSelect}
          isRTL={isRTL}
          monthFormat={monthFormat}
          hideKeyboardShortcutsPanel={hideKeyboardShortcutsPanel}
          renderNavNextButton={(navProps) =>
            renderNavNextButton({
              ...navProps,
              onClick: (event) => handleNextMonthClick(event, navProps.onClick),
            })
          }
          renderNavPrevButton={(navProps) =>
            renderNavPrevButton({
              ...navProps,
              onClick: (event) => handlePrevMonthClick(event, navProps.onClick),
            })
          }
          {...props}
        />
      )}
      {/* In responsive, different component will be rendered. (FlightDatePicker.tsx Line:123) */}
      {showFooter &&
      xl &&
      (startDate || useDesktopSearchbarFooter) &&
      (!shouldShowHolidayTabs || activeHolidayTab === 'calendar') ? (
        <DateRangePickerFooter
          $holidayMode={isHoliday}
          $desktopSearchbarFooter={useDesktopSearchbarFooter}
          $holidaySummaryVisible={
            isHoliday && visibleOfficialHolidayNames.length > 0
          }
        >
          {isHoliday && visibleOfficialHolidayNames.length > 0 ? (
            <CalendarHolidayFooterSummary data-testid='calendar-holiday-footer'>
              {visibleOfficialHolidayNames.map((holidayName) => (
                <CalendarHolidayFooterItem key={holidayName}>
                  <CalendarHolidayFooterDot />
                  {holidayName}
                </CalendarHolidayFooterItem>
              ))}
            </CalendarHolidayFooterSummary>
          ) : null}
          {!isHoliday && !useDesktopSearchbarFooter ? (
            <Row>
              <span>{formatLocalDate(formatMomentDate(startDate))}</span>
              <FlexibleDiv left={8} top={3} right={8}>
                <CustomIcon
                  paletteColor='grey500'
                  icon={Compare}
                  fontSize={16}
                />
              </FlexibleDiv>
              <span>{formatLocalDate(formatMomentDate(endDate))}</span>
            </Row>
          ) : null}
          {onClose ? (
            <StyledButton
              onClick={handleApply}
              disabled={!startDate}
              $holidayMode={isHoliday}
              $desktopSearchbarFooter={useDesktopSearchbarFooter}
            >
              Uygula
            </StyledButton>
          ) : null}
        </DateRangePickerFooter>
      ) : null}
    </DateRangePickerWrapper>
  );
};

export default DateRangePicker;
